import { diffWords, lineDiff, type Change } from 'diff'

export const TEXT_DIFF_MAX_BYTES = 500 * 1024
export const TEXT_DIFF_MAX_LINES = 5_000
const DIFF_TIMEOUT_MS = 1_500
const MAX_EDIT_LENGTH = 10_000
const CONTEXT_LINES = 3

export interface TextDiffOptions {
  ignoreCase: boolean
  ignoreWhitespace: boolean
}

export interface TextDiffSegment {
  value: string
  type: 'same' | 'added' | 'removed'
}

export interface TextDiffRow {
  id: string
  type: 'same' | 'added' | 'removed' | 'collapsed' | 'marker'
  oldLine?: number
  newLine?: number
  value: string
  segments?: TextDiffSegment[]
  collapsedCount?: number
}

export interface TextDiffResult {
  rows: TextDiffRow[]
  unified: string
  additions: number
  removals: number
  changed: boolean
}

export type TextDiffLimit = 'bytes' | 'lines' | null

export interface TextDiffGuards {
  timeoutMs?: number
  maxEditLength?: number
}

export function textDiffLimit(text: string): TextDiffLimit {
  // UTF-8 always uses at least one byte per UTF-16 code unit. This safely
  // rejects grossly oversized paste input without first allocating its full
  // encoded representation on the UI thread.
  if (text.length > TEXT_DIFF_MAX_BYTES) return 'bytes'
  if (new TextEncoder().encode(text).byteLength > TEXT_DIFF_MAX_BYTES) return 'bytes'
  if (text.split('\n').length > TEXT_DIFF_MAX_LINES) return 'lines'
  return null
}

/** Keeps SSR and hydration output stable regardless of the host locale. */
export function formatTextDiffLimit(): { size: string, lines: string } {
  return {
    size: String(Math.round(TEXT_DIFF_MAX_BYTES / 1024)),
    lines: String(TEXT_DIFF_MAX_LINES),
  }
}

function linesFromChange(change: Change): string[] {
  const lines = change.value.split('\n')
  if (lines.at(-1) === '') lines.pop()
  return lines
}

function segments(oldValue: string, newValue: string, type: 'removed' | 'added', ignoreCase: boolean) {
  const source = type === 'removed' ? oldValue : newValue
  let sourceOffset = 0

  return diffWords(oldValue, newValue, { ignoreCase, timeout: 100 })?.flatMap((change) => {
    const segmentType = change.added ? 'added' as const : change.removed ? 'removed' as const : 'same' as const
    if (segmentType !== 'same' && segmentType !== type) return []

    // With ignoreCase, `diff` may give a common fragment from the other side.
    // Re-slice it from the rendered side so removed rows never display modified
    // casing (and added rows retain their own spelling too).
    const value = source.slice(sourceOffset, sourceOffset + change.value.length)
    sourceOffset += change.value.length
    return [{ value, type: segmentType }]
  })
}

function collapseUnchanged(rows: TextDiffRow[]): TextDiffRow[] {
  const result: TextDiffRow[] = []
  let index = 0
  while (index < rows.length) {
    if (rows[index]?.type !== 'same') {
      result.push(rows[index]!)
      index += 1
      continue
    }

    let end = index
    while (rows[end]?.type === 'same') end += 1
    const run = rows.slice(index, end)
    if (run.length <= CONTEXT_LINES * 2 + 1) {
      result.push(...run)
    } else {
      result.push(...run.slice(0, CONTEXT_LINES))
      result.push({
        id: `collapsed-${index}-${end}`,
        type: 'collapsed',
        value: '',
        collapsedCount: run.length - CONTEXT_LINES * 2,
      })
      result.push(...run.slice(-CONTEXT_LINES))
    }
    index = end
  }
  return result
}

function buildResult(
  changes: Change[],
  options: TextDiffOptions,
  original: string,
  modified: string,
): TextDiffResult {
  const rows: TextDiffRow[] = []
  const unified: string[] = ['--- original', '+++ modified']
  const originalLines = original.split('\n').length
  const modifiedLines = modified.split('\n').length
  const originalHasFinalNewline = original.endsWith('\n')
  const modifiedHasFinalNewline = modified.endsWith('\n')
  let oldLine = 1
  let newLine = 1
  let additions = 0
  let removals = 0

  for (let index = 0; index < changes.length; index += 1) {
    const change = changes[index]!
    const next = changes[index + 1]
    const values = linesFromChange(change)
    const pairedValues = change.removed && next?.added ? linesFromChange(next) : []

    values.forEach((value, valueIndex) => {
      const type = change.added ? 'added' : change.removed ? 'removed' : 'same'
      const row: TextDiffRow = {
        id: `${index}-${valueIndex}-${oldLine}-${newLine}`,
        type,
        oldLine: type === 'added' ? undefined : oldLine,
        newLine: type === 'removed' ? undefined : newLine,
        value,
      }
      if (type === 'removed' && pairedValues[valueIndex] !== undefined) {
        row.segments = segments(value, pairedValues[valueIndex]!, 'removed', options.ignoreCase)
      } else if (type === 'added' && changes[index - 1]?.removed) {
        const oldValues = linesFromChange(changes[index - 1]!)
        if (oldValues[valueIndex] !== undefined) {
          row.segments = segments(oldValues[valueIndex]!, value, 'added', options.ignoreCase)
        }
      }
      rows.push(row)
      unified.push(`${type === 'added' ? '+' : type === 'removed' ? '-' : ' '}${value}`)
      const marksOriginalEof = type === 'removed'
        && !originalHasFinalNewline
        && oldLine === originalLines
      const marksModifiedEof = type === 'added'
        && !modifiedHasFinalNewline
        && newLine === modifiedLines
      if (marksOriginalEof || marksModifiedEof) {
        const marker = '\\ No newline at end of file'
        rows.push({
          id: `marker-${index}-${valueIndex}-${type}`,
          type: 'marker',
          value: marker,
        })
        unified.push(marker)
      }
      if (type !== 'added') oldLine += 1
      if (type !== 'removed') newLine += 1
      if (type === 'added') additions += 1
      if (type === 'removed') removals += 1
    })
  }

  return {
    rows: collapseUnchanged(rows),
    unified: unified.join('\n'),
    additions,
    removals,
    changed: additions > 0 || removals > 0,
  }
}

export function compareText(
  original: string,
  modified: string,
  options: TextDiffOptions,
  guards: TextDiffGuards = {},
): Promise<TextDiffResult | undefined> {
  return new Promise((resolve) => {
    lineDiff.diff(original, modified, {
      ignoreCase: options.ignoreCase,
      ignoreWhitespace: options.ignoreWhitespace,
      stripTrailingCr: true,
      timeout: guards.timeoutMs ?? DIFF_TIMEOUT_MS,
      maxEditLength: guards.maxEditLength ?? MAX_EDIT_LENGTH,
      callback: (changes) => resolve(
        changes ? buildResult(changes, options, original, modified) : undefined,
      ),
    })
  })
}
